import React from 'react';
import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { getFormattedDate } from '../../../../../components/StepsListAsTiles/GetFormattedDate';
import {
  ButtonPriority as ButtonPRIORITY,
  ButtonSize,
  Spinner,
  SpinnerTypes,
  Text,
  TextTypography,
} from '@wix/wix-ui-tpa/cssVars';
import {
  ChevronLeftSmall,
  ChevronRightSmall,
} from '@wix/wix-ui-icons-common/on-stage';
import { StepUndo, useStepIsUnavailable } from '../StepControlsV2';
import { ActionBlockerAppearance } from '../../../../../../../../components-shared/ActionBlocker/ActionBlocker';
import { PreviewActionBlockerContainer } from '../../../../../../../../components-shared/ActionBlocker/PreviewActionBlockerContainer';
import { SidebarLayoutButton } from '../../../../../components/Buttons/SidebarLayoutButton/SidebarLayoutButton';
import { useQuiz } from '../../../../../../../../contexts/Quiz/QuizContext';
import { useSidebarLayoutBase } from '../../../contexts/SidebarLayoutBase/SidebarLayoutBaseContext';
import { useSettings } from '@wix/tpa-settings/react';
import { useResolveStep } from '../../../../../../../../contexts/ResolveStep/ResolveStepContext';
import utils, {
  ChallengeEntityType,
  IFollowingEntity,
  isStepResolved,
} from '../../../../utils';
import { memberWebAppButtonClick } from '@wix/bi-logger-challenges-member-web/v2';
import { ButtonNames } from '../../../../../../../../contexts/main/biInterfaces';
import { sidebarLayoutDataHooks } from '../../../sidebarLayoutDataHooks';

import { SidebarControlsContainer } from '../../../../../components/SidebarControlsContainer/SidebarControlsContainer';
import { FCWithChildren } from '@wix/challenges-web-library';
import challengeSettings from '../../../../../../settingsParams';
import { IControl } from '../utils/types';

import {
  FocusPoint,
  useFocusManager,
} from '../../../../../../../../contexts/FocusManager';
import { useCSSPBEnabled } from '../../../../../../../../hooks/useCSSPBStyle';
import { classes, st } from './FormQuizControls.st.css';

export interface IFormQuizProps extends Pick<IControl, 'steps' | 'sections'> {
  isDisabled?: boolean;
  onNextEntity(nextEntity: IFollowingEntity): Promise<void>;
}

export const FormQuizControlsV2: FCWithChildren<IFormQuizProps> = ({
  isDisabled,
  onNextEntity,
  steps,
  sections,
}) => {
  const { t } = useTranslation();
  const { isMobile, isRTL, language } = useEnvironment();
  const settings = useSettings();
  const alignment = utils.getContentAlignByType(settings, isRTL);
  const {
    controllerIsReady,
    submission,
    sendSubmissionInProgress,
    earnedGrade,
    resubmitTheQuiz,
  } = useQuiz();
  const { currentStep, formQuizRef, formValidity } = useSidebarLayoutBase();
  const { isResolveStepRequestInProgress } = useResolveStep();
  const bi = useBi();
  const cssPBEnabled = useCSSPBEnabled({
    includeParticipantPageAlignmentCheck: true,
  });

  const isFilled = !!submission;
  const passingGrade = utils.getPassingGrade(currentStep, isFilled);
  const hasPassingGrade = !!passingGrade;
  const isQuizPassed = !hasPassingGrade || earnedGrade >= passingGrade;

  const isUnresolved = !isStepResolved(currentStep);
  const isQuizResolved = !isUnresolved;
  const isResubmitAvailable = isFilled && hasPassingGrade && !isQuizPassed;
  const {
    isSectionLocked,
    isStepUnavailable,
    isLockedButVisible,
    isFutureScheduledStep,
    firstAvailableStep,
  } = useStepIsUnavailable();
  const { setNextFocusPoint } = useFocusManager();

  const previousEntity = utils.getFollowingEntity(
    currentStep?.id,
    { listParticipantSections: sections, participantSteps: steps },
    'previous',
  );
  const nextEntity = utils.getFollowingEntity(currentStep?.id, {
    listParticipantSections: sections,
    participantSteps: steps,
  });

  const isPreviousButtonShown =
    previousEntity.type !== ChallengeEntityType.VOID;
  const isFinalStep = nextEntity.type === ChallengeEntityType.VOID;

  const submit = () => {
    setNextFocusPoint(FocusPoint.Bottom);
    if (!formQuizRef?.current) {
      console.error("Can't find the form component.");
      return;
    }

    formQuizRef.current?.validate().then(
      (isValid) => {
        if (isValid) {
          void bi.report(
            memberWebAppButtonClick({
              buttonName: ButtonNames.QuizSubmit,
            }),
          );
          void formQuizRef.current?.submitTheForm();
        }
      },
      (err) => {
        console.error("Can't submit the form.", err);
      },
    );
  };

  const resubmit = () => {
    void bi.report(
      memberWebAppButtonClick({
        buttonName: ButtonNames.QuizResubmit,
      }),
    );

    resubmitTheQuiz();
  };

  const showSubmitButton =
    isUnresolved &&
    !isResubmitAvailable &&
    !isStepUnavailable &&
    controllerIsReady &&
    !isSectionLocked;

  function getMsg() {
    // You’ll be able to complete this step on Mon, Mar 16, 2026
    if (isFutureScheduledStep) {
      return t('challenge.page.step-unavailable.warning', {
        date: getFormattedDate(language, currentStep?.dateFrame?.start),
      });
    }

    // This step is locked. Complete the required step first. ?
    if (isLockedButVisible) {
      return t('challenge.page.steps.locked-required-step-warning');
    }

    if (isFilled) {
      return isQuizPassed
        ? t('live-site.step-quiz.completed-msg')
        : t('live-site.step-quiz.failed-msg');
    }

    if (hasPassingGrade) {
      return t('live-site.step-quiz.completed-msg-with-score', {
        score: passingGrade,
      });
    }
    return null;
  }

  const msg = getMsg();

  const msgLine =
    msg && !isResolveStepRequestInProgress ? (
      <Text
        tagName="p"
        typography={TextTypography.runningText}
        className={classes.message}
      >
        {msg}
      </Text>
    ) : null;

  const PreviousButton = isPreviousButtonShown ? (
    <SidebarLayoutButton
      dataHook={sidebarLayoutDataHooks.stepPreviousBtn()}
      className={classes.previousButton}
      withDefaultTheme={true}
      disabled={isResolveStepRequestInProgress}
      fullWidth={isMobile}
      size={isMobile ? ButtonSize.small : ButtonSize.medium}
      priority={ButtonPRIORITY.secondary}
      prefixIcon={<ChevronLeftSmall className={classes.ltrArrow} />}
      suffixIcon={<ChevronRightSmall className={classes.rtlArrow} />}
      moveFocus={false}
      onClick={async () => {
        setNextFocusPoint(FocusPoint.Top);
        await onNextEntity(previousEntity);
      }}
    >
      {settings.get(challengeSettings.prevButtonTxt) as string}
    </SidebarLayoutButton>
  ) : null;

  function renderCTAButton() {
    if (sendSubmissionInProgress || isResolveStepRequestInProgress) {
      return <Spinner type={SpinnerTypes.slim} diameter={40} />;
    }
    if (isLockedButVisible || isFutureScheduledStep) {
      return (
        <SidebarLayoutButton
          className={classes.completeBtn}
          withDefaultTheme={true}
          fullWidth={isMobile}
          size={isMobile ? ButtonSize.small : ButtonSize.medium}
          suffixIcon={<ChevronRightSmall className={classes.ltrArrow} />}
          prefixIcon={<ChevronLeftSmall className={classes.rtlArrow} />}
          onClick={() => {
            setNextFocusPoint(FocusPoint.Top);
            void onNextEntity({
              step: firstAvailableStep,
              type: ChallengeEntityType.STEP,
              id: firstAvailableStep?.id,
            });
          }}
        >
          {t('challenge.page.steps.go-to-required-step')}
        </SidebarLayoutButton>
      );
    }
    if (isQuizResolved && !isFinalStep) {
      return (
        <SidebarLayoutButton
          className={classes.completeBtn}
          dataHook={sidebarLayoutDataHooks.stepNextBtn()}
          withDefaultTheme={true}
          fullWidth={isMobile}
          size={isMobile ? ButtonSize.small : ButtonSize.medium}
          suffixIcon={<ChevronRightSmall className={classes.ltrArrow} />}
          prefixIcon={<ChevronLeftSmall className={classes.rtlArrow} />}
          onClick={() => {
            setNextFocusPoint(FocusPoint.Top);
            void onNextEntity(nextEntity);
          }}
        >
          {settings.get(challengeSettings.nextButtonTxt) as string}
        </SidebarLayoutButton>
      );
    }
    if (showSubmitButton) {
      return (
        <PreviewActionBlockerContainer
          appearance={ActionBlockerAppearance.PopoverAppearance}
          content={t('action-blocker.preview-mode.complete-step')}
        >
          <SidebarLayoutButton
            className={classes.completeBtn}
            dataHook={sidebarLayoutDataHooks.quizSubmitBtn({
              resubmit: false,
            })}
            disabled={
              isDisabled || formValidity?.length > 0 || sendSubmissionInProgress
            }
            fullWidth={isMobile}
            size={isMobile ? ButtonSize.small : ButtonSize.medium}
            onClick={submit}
          >
            {t('live-site.step-quiz.complete-btn')}
          </SidebarLayoutButton>
        </PreviewActionBlockerContainer>
      );
    }
    return null;
  }

  const undoButtonLabel = settings.get(
    challengeSettings.retakeQuizButtonTxt,
  ) as string;

  const UndoButton = isResubmitAvailable ? (
    <StepUndo forceVisible={true} onClick={resubmit}>
      {undoButtonLabel}
    </StepUndo>
  ) : (
    <StepUndo>{undoButtonLabel}</StepUndo>
  );

  return (
    <SidebarControlsContainer>
      <div
        className={st(classes.root, {
          mobile: isMobile,
          alignment: !cssPBEnabled ? alignment : null,
          withCSSAlignment: cssPBEnabled,
          rtl: isRTL,
        })}
      >
        <div className={classes.secondaryControls}>
          {PreviousButton}
          {isMobile && UndoButton}
        </div>

        {msgLine}
        <div className={classes.mainControls}>
          {!isMobile && UndoButton}
          {renderCTAButton()}
        </div>
      </div>
    </SidebarControlsContainer>
  );
};
